// src/pages/Seo.js
import React, { useEffect, useState, useContext } from 'react';
import toast from 'react-hot-toast';
import { FaSearch, FaSave, FaTrash, FaPlus, FaUserPlus } from 'react-icons/fa';
import AuthContext from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const TABS = [
  { id: 'pages', label: 'Page Meta' },
  { id: 'tracking', label: 'Verification & Tracking' },
  { id: 'robots', label: 'robots.txt' },
  { id: 'sitemap', label: 'Sitemap' },
];

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

const input = 'w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all';

const Seo = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user && user.role === 'admin';

  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('pages');
  const [selectedPath, setSelectedPath] = useState('/');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // SEO users (admin only)
  const [seoUsers, setSeoUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });

  useEffect(() => {
    fetchConfig();
    if (isAdmin) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API}/api/seo`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load SEO settings');
      const data = await res.json();
      setCfg(data);
      if (data.knownPaths && data.knownPaths.length) setSelectedPath(data.knownPaths[0]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/api/seo/users`, { headers: authHeaders() });
      if (res.ok) setSeoUsers(await res.json());
    } catch (_) { /* ignore */ }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/seo`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          default: cfg.default,
          pages: cfg.pages,
          tracking: cfg.tracking,
          robots: cfg.robots,
          sitemap: cfg.sitemap,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Save failed');
      setCfg(data.config);
      toast.success('SEO settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setPageField = (field, value) => {
    setCfg((c) => ({
      ...c,
      pages: { ...c.pages, [selectedPath]: { ...c.pages[selectedPath], [field]: value } },
    }));
  };
  const setDefault = (field, value) => setCfg((c) => ({ ...c, default: { ...c.default, [field]: value } }));
  const setTracking = (field, value) => setCfg((c) => ({ ...c, tracking: { ...c.tracking, [field]: value } }));

  const setSitemapRow = (i, field, value) => {
    setCfg((c) => {
      const urls = [...(c.sitemap.urls || [])];
      urls[i] = { ...urls[i], [field]: value };
      return { ...c, sitemap: { ...c.sitemap, urls } };
    });
  };
  const addSitemapRow = () => setCfg((c) => ({ ...c, sitemap: { ...c.sitemap, urls: [...(c.sitemap.urls || []), { loc: '/', changefreq: 'weekly', priority: '0.7' }] } }));
  const removeSitemapRow = (i) => setCfg((c) => ({ ...c, sitemap: { ...c.sitemap, urls: c.sitemap.urls.filter((_, idx) => idx !== i) } }));

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/seo/users`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(newUser),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create');
      toast.success('SEO user created');
      setNewUser({ name: '', email: '', password: '' });
      fetchUsers();
    } catch (err) { toast.error(err.message); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Remove this SEO user?')) return;
    try {
      const res = await fetch(`${API}/api/seo/users/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to remove');
      toast.success('Removed');
      fetchUsers();
    } catch (err) { toast.error(err.message); }
  };

  if (loading || !cfg) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  const page = cfg.pages[selectedPath] || {};
  const allTabs = isAdmin ? [...TABS, { id: 'users', label: 'SEO Access' }] : TABS;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
            <FaSearch className="text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">SEO Manager</h1>
            <p className="text-slate-500 text-sm">Edit meta tags, schema, tracking, robots & sitemap — changes go live instantly.</p>
          </div>
        </div>
        {tab !== 'users' && (
          <button onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-500/30 disabled:opacity-50">
            <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200">
        {allTabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${tab === t.id ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* PAGE META */}
      {tab === 'pages' && (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="md:col-span-1">
            <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Pages</p>
            <div className="space-y-1">
              {(cfg.knownPaths || Object.keys(cfg.pages)).map((p) => (
                <button key={p} onClick={() => setSelectedPath(p)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${selectedPath === p ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {p === '/' ? '/ (Home)' : p}
                </button>
              ))}
            </div>

            <div className="mt-6 bg-slate-50 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase">Site Defaults</p>
              <Field label="Home title">
                <input className={input} value={cfg.default.homeTitle || ''} onChange={(e) => setDefault('homeTitle', e.target.value)} />
              </Field>
              <Field label="Title suffix" hint="Appended after each page title.">
                <input className={input} value={cfg.default.titleSuffix || ''} onChange={(e) => setDefault('titleSuffix', e.target.value)} />
              </Field>
              <Field label="Default description">
                <textarea rows="3" className={input} value={cfg.default.description || ''} onChange={(e) => setDefault('description', e.target.value)} />
              </Field>
              <Field label="Default social image URL">
                <input className={input} value={cfg.default.image || ''} onChange={(e) => setDefault('image', e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="md:col-span-3 bg-white rounded-xl border border-slate-100 shadow-card p-6 space-y-4">
            <h3 className="font-semibold text-slate-800">Editing: <span className="text-primary-600">{selectedPath === '/' ? 'Home' : selectedPath}</span></h3>
            <Field label="Meta title" hint="Leave blank to use the default. Shown as the clickable headline in Google.">
              <input className={input} value={page.title || ''} onChange={(e) => setPageField('title', e.target.value)} placeholder="e.g. Handmade Crochet Bags" />
            </Field>
            <Field label="Meta description" hint="~150-160 characters. The grey text under the title in search results.">
              <textarea rows="3" className={input} value={page.description || ''} onChange={(e) => setPageField('description', e.target.value)} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Keywords" hint="Comma separated (low SEO weight).">
                <input className={input} value={page.keywords || ''} onChange={(e) => setPageField('keywords', e.target.value)} />
              </Field>
              <Field label="Canonical URL" hint="Leave blank to auto-generate.">
                <input className={input} value={page.canonical || ''} onChange={(e) => setPageField('canonical', e.target.value)} />
              </Field>
            </div>
            <Field label="Social share image URL" hint="Override the default Open Graph image for this page.">
              <input className={input} value={page.image || ''} onChange={(e) => setPageField('image', e.target.value)} />
            </Field>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!page.noindex} onChange={(e) => setPageField('noindex', e.target.checked)} className="w-5 h-5 text-primary-600 rounded" />
              <span className="text-sm font-medium text-slate-700">Hide this page from search engines (noindex)</span>
            </label>
            <Field label="Structured data (JSON-LD)" hint="Paste valid JSON for rich results. Invalid JSON is ignored automatically.">
              <textarea rows="6" className={`${input} font-mono text-xs`} value={page.jsonLd || ''} onChange={(e) => setPageField('jsonLd', e.target.value)} placeholder='{"@context":"https://schema.org", ...}' />
            </Field>
          </div>
        </div>
      )}

      {/* TRACKING */}
      {tab === 'tracking' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-card p-6 max-w-2xl space-y-4">
          <p className="text-sm text-slate-500">Paste only the ID / token — we insert the correct code on every page for you.</p>
          <Field label="Google Search Console verification" hint='The content value of the "google-site-verification" meta tag.'>
            <input className={input} value={cfg.tracking.gscVerification || ''} onChange={(e) => setTracking('gscVerification', e.target.value)} />
          </Field>
          <Field label="Bing verification token">
            <input className={input} value={cfg.tracking.bingVerification || ''} onChange={(e) => setTracking('bingVerification', e.target.value)} />
          </Field>
          <Field label="Google Analytics 4 ID" hint="Looks like G-XXXXXXXXXX">
            <input className={input} value={cfg.tracking.ga4Id || ''} onChange={(e) => setTracking('ga4Id', e.target.value)} placeholder="G-XXXXXXXXXX" />
          </Field>
          <Field label="Google Tag Manager ID" hint="Looks like GTM-XXXXXXX">
            <input className={input} value={cfg.tracking.gtmId || ''} onChange={(e) => setTracking('gtmId', e.target.value)} placeholder="GTM-XXXXXXX" />
          </Field>
          <Field label="Meta (Facebook) Pixel ID" hint="Numeric ID">
            <input className={input} value={cfg.tracking.fbPixelId || ''} onChange={(e) => setTracking('fbPixelId', e.target.value)} />
          </Field>
        </div>
      )}

      {/* ROBOTS */}
      {tab === 'robots' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-card p-6 max-w-3xl">
          <Field label="robots.txt" hint="Served live at suruchiblossom.com/robots.txt">
            <textarea rows="16" className={`${input} font-mono text-xs`} value={cfg.robots || ''} onChange={(e) => setCfg((c) => ({ ...c, robots: e.target.value }))} />
          </Field>
        </div>
      )}

      {/* SITEMAP */}
      {tab === 'sitemap' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">Served live at suruchiblossom.com/sitemap.xml</p>
            <button onClick={addSitemapRow} className="flex items-center gap-2 text-sm text-primary-600 font-medium hover:text-primary-700">
              <FaPlus /> Add URL
            </button>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-400 uppercase px-1">
              <span className="col-span-5">URL / Path</span>
              <span className="col-span-3">Change frequency</span>
              <span className="col-span-3">Priority</span>
              <span className="col-span-1"></span>
            </div>
            {(cfg.sitemap.urls || []).map((u, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input className={`${input} col-span-5`} value={u.loc} onChange={(e) => setSitemapRow(i, 'loc', e.target.value)} placeholder="/products" />
                <select className={`${input} col-span-3`} value={u.changefreq || 'weekly'} onChange={(e) => setSitemapRow(i, 'changefreq', e.target.value)}>
                  {['always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never'].map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <input className={`${input} col-span-3`} value={u.priority || ''} onChange={(e) => setSitemapRow(i, 'priority', e.target.value)} placeholder="0.7" />
                <button onClick={() => removeSitemapRow(i)} className="col-span-1 text-red-500 hover:text-red-600 flex justify-center"><FaTrash /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SEO ACCESS (admin only) */}
      {tab === 'users' && isAdmin && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
            <h3 className="font-semibold text-slate-800 mb-4">SEO logins</h3>
            {seoUsers.length === 0 ? (
              <p className="text-sm text-slate-500">No SEO users yet. Create one to give your SEO person access to only this section.</p>
            ) : (
              <div className="space-y-2">
                {seoUsers.map((u) => (
                  <div key={u._id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{u.name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                    <button onClick={() => deleteUser(u._id)} className="text-red-500 hover:text-red-600"><FaTrash /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><FaUserPlus className="text-primary-600" /> Create SEO login</h3>
            <form onSubmit={createUser} className="space-y-4">
              <Field label="Name">
                <input className={input} value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required />
              </Field>
              <Field label="Email">
                <input type="email" className={input} value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
              </Field>
              <Field label="Temporary password" hint="Min 8 characters. Share it with your SEO person.">
                <input type="text" className={input} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
              </Field>
              <button type="submit" className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all">
                Create SEO User
              </button>
            </form>
            <p className="text-xs text-slate-400 mt-3">This account can edit only SEO settings — it cannot see orders, customers, payments or other settings.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Seo;
